<template>
  <component :is="tag" data-component-name="Cards.MediaSpokesPeople">
    <div class="left">
      <NuxtImg
        v-if="avatar"
        :src="avatar"
        :alt="fullname + ' photo'"
        preload
        format="webp"
        sizes="xs:200px md:200px xl:280px"
        densities="1x 2x"
      />

      <a v-if="download_url" :href="download_url" download>
        <Buttons.Base fill="none" accent="black">
          {{ $t('Download') }}
          <SvgIcon name="download" />
        </Buttons.Base>
      </a>
    </div>

    <div class="right">
      <h4>{{ fullname }}</h4>
      <strong>{{ position }}</strong>
      <div class="description" v-html="description" />

      <ul v-if="socialLinks.length">
        <li v-for="({ to, icon: name }, key) in socialLinks" :key>
          <NuxtLink :to target="_blank">
            <SvgIcon :name />
          </NuxtLink>
        </li>
      </ul>
    </div>
  </component>
</template>

<script setup lang="ts">
// components
import Buttons from '~/components/redesign/Buttons';

// utils
import { toSafeArray } from '~/utils';

export type CardsMediaSpokesPeopleProps = {
  tag?: keyof HTMLElementTagNameMap;
  avatar: string;
  fullname: string;
  position: string;
  description: string;

  linkedin?: string;
  twitter?: string;
  facebook?: string;
  reddit?: string;

  download_url?: string;
};

const props = withDefaults(defineProps<CardsMediaSpokesPeopleProps>(), {
  tag: 'div',
});

const SOCIAL_LINKS_KEYS = [
  { key: 'linkedin', icon: 'linked-in' },
  { key: 'facebook', icon: 'facebook' },
  { key: 'twitter', icon: 'x' },
  { key: 'reddit', icon: 'reddit' },
];

const socialLinks = computed<{ to: string; icon: string }[]>(() => {
  return toSafeArray(
    SOCIAL_LINKS_KEYS.map(({ key, icon }) => {
      if (props[key]) {
        return {
          to: props[key],
          icon,
        };
      }
    }),
  );
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/mixins/typo';
@use '_/mixins/component';
@use '_/variables/shadow';
@use '_/fn';

[data-component-name='Cards.MediaSpokesPeople'] {
  @include flex.start-start;
  gap: 2rem;
  padding: 2rem;

  box-shadow: shadow.$elevation-1;

  @include media.tablet {
    align-items: center;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  @include media.mobile {
    gap: 1.2rem;
    padding: 1.5rem 1rem;
    flex-direction: column;
  }

  .left {
    @include flex.stretch-center;
    flex-direction: column;

    min-width: 17.5rem;

    @include media.tablet {
      min-width: 12.5rem;
    }

    @include media.mobile {
      width: 100%;
      max-width: 12.5rem;
      min-width: unset;
    }

    img {
      margin-bottom: 0.5rem;

      @include media.tablet {
        margin-bottom: 0;
      }
    }

    a {
      display: block;

      [data-component-name='Buttons.Base'] {
        @include typo.button-medium;
      }
    }
  }

  .right {
    @include flex.start-start;
    flex-direction: column;
    gap: 1rem 0;
    align-self: center;

    @include media.tablet {
      gap: 0.5rem 0;
    }

    h4 {
      @include typo.h4;
    }

    strong {
      @include typo.subtitle-1;
    }

    .description {
      @include typo.body-3;
    }

    ul {
      @include flex.center-center;
      gap: 1rem;

      li {
        a {
          @include flex.center-center;

          svg {
            @include size.fixed(2rem);
            fill: fn.token('surf-cont-secon-action');
          }
        }
      }
    }
  }

  &:not(:last-of-type) {
    margin-bottom: 1.8rem;

    @include media.tablet {
      margin-bottom: 1.5rem;
    }
  }
}
</style>
